/**
 * « DEMANDER UN CHANGEMENT » — la matière du prompt, calculée à part et sans effet de bord.
 *
 * Deux choses que la retouche du 21/08 ne faisait pas, et qui se voyaient dans le texte rendu :
 *
 *  1. LE MODÈLE NE SAVAIT PAS OÙ IL ÉCRIVAIT. Le brief partait avec le SLUG en guise de marque, la
 *     consigne recopiée à la place de « ce que vend la boutique », et rien sur le produit ni sur la
 *     page. Un « rends ça plus vendeur » se retrouvait donc à réécrire un titre sans savoir ce qui est
 *     vendu — et la seule chose qui rattrapait le sujet était le texte actuel du champ.
 *  2. LA RETOUCHE PRENAIT TOUTE LA SECTION. La vendeuse clique UN bloc, écrit « plus court », et les
 *     six autres blocs changeaient aussi. {@link slotInScope} rend à la retouche la portée de la
 *     sélection : la section, le bloc, ou le seul champ visé.
 *
 * Pur : des chaînes entrent, des chaînes sortent. Les longueurs sont bornées ICI parce que le serveur
 * les valide (400 au-delà) — un contexte trop riche ne doit jamais transformer une retouche en refus.
 */

import { bt, btp } from '../../i18n';

/** Ce que la vendeuse a désigné dans le canvas — du plus large au plus étroit. */
export interface RetouchScope {
  sectionId: string;
  /** Un bloc de la section (ou un bloc imbriqué) — sa branche entière est retouchée. */
  blockId?: string;
  /** Un champ de la SECTION elle-même (le « hotspot » du canvas) — lui seul est retouché. */
  fieldId?: string;
}

/**
 * Ce que la pastille transmet en plus de la consigne : la portée, et l'IA choisie pour CE prompt.
 *
 * Les deux fournisseurs sont séparés parce qu'une clé sert ce que SON fournisseur sait faire :
 * Anthropic écrit sans dessiner, Higgsfield dessine sans écrire. Un menu unique mentirait sur l'un
 * des deux. Absents = la chaîne rangée dans les Réglages sert, comme avant.
 */
export interface RetouchOptions {
  scope?: RetouchScope;
  textProvider?: string;
  imageProvider?: string;
}

/** Bornes du serveur (AiController) — dépasser, c'est un 400 sur une retouche qui aurait marché. */
const MAX_ROLE = 500;
const MAX_STORE_DESCRIPTION = 600;
const MAX_PRODUCT_CONTEXT = 3000;
const MAX_PRODUCT_DESCRIPTION = 700;
const MAX_OUTLINE = 12;

/**
 * Ce champ (ou ce bloc) fait-il partie de ce que la vendeuse a désigné ?
 *
 * L'adresse d'un champ est `[sectionId, ...blocIds] + fieldId` (ui-react) : un champ de la section a
 * un chemin d'UN élément, un champ de bloc porte tous ses parents. Un champ visé seul n'existe qu'au
 * niveau de la section — c'est ce que le canvas sait désigner (`data-aqx-field-id`).
 */
export function slotInScope(
  slot: { readonly path: readonly string[]; readonly fieldId: string },
  scope: RetouchScope,
): boolean {
  if (slot.path[0] !== scope.sectionId) return false;
  if (scope.fieldId) return slot.path.length === 1 && slot.fieldId === scope.fieldId;
  if (scope.blockId) return slot.path.includes(scope.blockId);
  return true;
}

/** Ce que la pastille annonce, et ce que le brief répète au modèle. */
export function scopeWording(
  scope: RetouchScope,
  names: { section: string; target?: string },
): string {
  if (scope.fieldId)
    return `le champ « ${names.target || scope.fieldId} » de la section « ${names.section} »`;
  if (scope.blockId)
    return `le bloc « ${names.target || 'sélectionné'} » de la section « ${names.section} »`;
  return `toute la section « ${names.section} »`;
}

/**
 * LA MÊME PORTÉE, mais pour la VENDEUSE — et c'est pourquoi elle est séparée de {@link scopeWording}.
 *
 * La pastille affichait le texte du PROMPT jusqu'au 15/09 : « Je retouche » passait par `bt()` et la
 * suite arrivait en français en dur, si bien qu'un éditeur allemand annonçait
 * « Ich überarbeite **le bloc « Titel » de la section « Hero »** ». Le prompt, lui, reste en français
 * — c'est la langue dans laquelle ce fichier parle au modèle, et la traduire n'aiderait personne.
 */
export function scopeLabel(
  scope: RetouchScope,
  names: { section: string; target?: string },
): string {
  if (scope.fieldId)
    return btp('chrome.ask.scope_field', 'the “{target}” field of the “{section}” section', {
      target: names.target || scope.fieldId,
      section: names.section,
    });
  if (scope.blockId)
    return btp('chrome.ask.scope_block', 'the “{target}” block of the “{section}” section', {
      target: names.target || bt('chrome.ask.the_block', 'selected'),
      section: names.section,
    });
  return btp('chrome.ask.scope_section', 'the whole “{section}” section', {
    section: names.section,
  });
}

export interface RetouchProduct {
  name: string;
  /** Description RÉELLE du produit, déjà débarrassée de son HTML. */
  description: string;
  /** Prix formaté tel que la boutique l'affiche, quand on l'a. */
  price?: string;
}

export interface RetouchBriefInput {
  /** Ce que la vendeuse a tapé. Vide = « améliore », mais la consigne reste la sienne d'abord. */
  instruction: string;
  storeName: string;
  /** Les produits réellement vendus — la seule source honnête de « ce que vend cette boutique ». */
  catalogNames: string[];
  /** Le produit auquel CETTE page est liée (page produit, ou produit d'aperçu). */
  product: RetouchProduct | null;
  /** `product` | `index` | `page`… + le nom de la template, tels que le builder les connaît. */
  pageTemplate: string;
  pageHandle: string;
  /** Les sections de la page, dans l'ordre, par leur nom d'écran. */
  pageOutline: string[];
  /** Nom d'écran de la section retouchée. */
  sectionName: string;
  /** Ce que la sélection vise, en toutes lettres — voir {@link scopeWording}. */
  scopeWording: string;
}

/** Les quatre champs du brief serveur, remplis pour une RETOUCHE. */
export interface RetouchBrief {
  role: string;
  storeName: string;
  storeDescription: string;
  productContext: string;
}

const PAGE_KIND: Record<string, string> = {
  index: "page d'accueil",
  product: 'page produit',
  collection: 'page collection',
  page: 'page de contenu',
  cart: 'page panier',
  search: 'page de recherche',
};

/**
 * LE CONTEXTE QUE LE MODÈLE REÇOIT — la boutique, sa page, son produit, puis la consigne.
 *
 * L'ordre n'est pas décoratif : le serveur monte le message dans cet ordre (`GenerationPrompt.user`)
 * et la consigne du vendeur arrive EN DERNIER, juste avant les champs — c'est elle qui doit rester
 * sous les yeux du modèle au moment où il écrit.
 */
export function buildRetouchBrief(input: RetouchBriefInput): RetouchBrief {
  const consigne = input.instruction.trim() || 'Améliore ce texte : plus clair, plus vendeur.';
  const kind = PAGE_KIND[input.pageTemplate] ?? `page « ${input.pageTemplate} »`;

  const productLines: string[] = [];
  if (input.product) {
    const desc = input.product.description
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, MAX_PRODUCT_DESCRIPTION);
    productLines.push(
      `LE PRODUIT DE CETTE PAGE : ${input.product.name}.` +
        (input.product.price ? ` Prix affiché : ${input.product.price}.` : '') +
        (desc ? ` Description réelle : ${desc}` : ''),
    );
  }
  const outline = input.pageOutline.filter(Boolean).slice(0, MAX_OUTLINE);
  if (outline.length > 0) {
    productLines.push(
      `CETTE PAGE (${kind}, « ${input.pageHandle} ») enchaîne : ${outline.join(' → ')}.`,
    );
  } else {
    productLines.push(`CETTE PAGE : ${kind} « ${input.pageHandle} ».`);
  }
  productLines.push(
    `TU RETOUCHES ${input.scopeWording} — les autres textes de la page ne changent pas, ` +
      'ce que tu écris doit rester cohérent avec eux.',
  );

  return {
    role: `Section « ${input.sectionName} ». Consigne du vendeur, à appliquer à chaque champ : ${consigne}`.slice(
      0,
      MAX_ROLE,
    ),
    storeName: (input.storeName || 'La boutique').slice(0, 120),
    storeDescription: input.catalogNames.length
      ? `Produits réellement vendus : ${input.catalogNames.join(' · ')}`.slice(
          0,
          MAX_STORE_DESCRIPTION,
        )
      : '',
    productContext: productLines.join('\n').slice(0, MAX_PRODUCT_CONTEXT),
  };
}

/**
 * LE SUJET D'UNE IMAGE RETOUCHÉE — même règle qu'au 21/08 (le sac à main dans une boutique de casques)
 * : ce qui est déjà écrit dans la section, puis le produit, puis le catalogue. Jamais la consigne seule.
 */
export function buildImageSubject(input: {
  sectionText: string;
  product: RetouchProduct | null;
  catalogNames: string[];
}): string {
  const parts = [
    input.sectionText
      ? `LE SUJET DE L'IMAGE (contenu actuel de la section, à conserver) : ${input.sectionText}`
      : '',
    input.product ? `Produit de la page : ${input.product.name}.` : '',
    input.catalogNames.length
      ? `Produits réellement vendus par la boutique : ${input.catalogNames.join(' · ')}.`
      : '',
  ];
  return parts.filter(Boolean).join(' ');
}
